/**
 * The page lifecycle as the screens need it: which steps a status offers, the
 * address tree, the SEO draft, the scheduled date.
 *
 * The rules repeat packages/domain/src/aggregates/page.ts (what each step
 * refuses, the 70 and 200 character limits, the slug shape) so a mistake is
 * caught while typing; the gateway stays the judge and its sentence is shown
 * as it comes. Pure, with `now` passed in, so the tests do not use the clock.
 */
import type { PageSeoDto, PageStatus } from '../api/endpoints.js'
import { fromLocalInputValue } from './schedule.js'

export const PAGE_STATUSES: readonly PageStatus[] = ['draft', 'review', 'published', 'trashed']

export function isPageStatus(value: string | null | undefined): value is PageStatus {
  return !!value && (PAGE_STATUSES as readonly string[]).includes(value)
}

/** The tone of the chip, which the stylesheet already has per status. */
export function pageStatusTone(status: PageStatus): PageStatus {
  return status
}

export interface PageFacts {
  status: PageStatus
  publishAt: number | null
  publishedAt: number | null
  hasUnpublishedChanges: boolean
}

/**
 * The «modifiche non pubblicate» badge. A page that was never published has
 * only unpublished changes by definition, and its status already says so:
 * the badge is for a page readers have seen and that has moved on since.
 */
export function showUnpublishedBadge(page: PageFacts): boolean {
  return page.status !== 'trashed' && page.hasUnpublishedChanges && page.publishedAt !== null
}

/** The date a published page is waiting for; null when it is not scheduled (or the date has passed). */
export function scheduledFor(page: Pick<PageFacts, 'status' | 'publishAt'>, now: number): number | null {
  return page.status === 'published' && page.publishAt !== null && page.publishAt > now ? page.publishAt : null
}

/** A page readers can open right now, once the site is published. */
export function isLiveCandidate(page: Pick<PageFacts, 'status' | 'publishAt' | 'publishedAt'>, now: number): boolean {
  if (page.status !== 'published') return false
  return page.publishAt === null || page.publishAt <= now
}

// ----- Lifecycle steps -----------------------------------------------------------

export type PageAction = 'submit' | 'return' | 'publish' | 'schedule' | 'unpublish' | 'discard' | 'trash' | 'restore'

/** Publishing and taking down need `cms:publish`; every other step `cms:design:write`. */
export function pageActionScope(action: PageAction): 'cms:publish' | 'cms:design:write' {
  return action === 'publish' || action === 'schedule' || action === 'unpublish' ? 'cms:publish' : 'cms:design:write'
}

/**
 * What a status offers, in the order the buttons appear (the first one is the
 * natural next step).
 *
 * - A draft goes to review or straight to publication; a page in review also
 *   comes back to draft.
 * - A published page is edited in place, so «Pubblica» only returns when
 *   something changed since (or a scheduled date can be brought forward);
 *   the date itself can always be set again.
 * - «Annulla le modifiche» needs a live revision to go back to.
 * - The trash only offers «Ripristina»: the gateway refuses everything else.
 */
export function pageActions(
  page: PageFacts & { hasLiveRevision: boolean },
  now: number,
): PageAction[] {
  if (page.status === 'trashed') return ['restore']
  const actions: PageAction[] = []
  if (page.status === 'draft') actions.push('submit', 'publish', 'schedule')
  else if (page.status === 'review') actions.push('publish', 'schedule', 'return')
  else {
    if (page.hasUnpublishedChanges || scheduledFor(page, now) !== null) actions.push('publish')
    actions.push('schedule')
  }
  if (page.hasLiveRevision && page.hasUnpublishedChanges) actions.push('discard')
  if (page.status === 'published') actions.push('unpublish')
  actions.push('trash')
  return actions
}

/** The steps this person may take, given their scopes. */
export function allowedPageActions(
  page: PageFacts & { hasLiveRevision: boolean },
  now: number,
  can: { write: boolean; publish: boolean },
): PageAction[] {
  return pageActions(page, now).filter((action) => pageActionScope(action) === 'cms:publish' ? can.publish : can.write)
}

export type ScheduleCheck =
  | { ok: true; publishAt: number; iso: string }
  | { ok: false; problem: 'missing' | 'past' }

/** The date typed in a `datetime-local` field, for the «Programma» step: it has to be in the future. */
export function checkPublishAt(input: string, now: number): ScheduleCheck {
  const publishAt = fromLocalInputValue(input)
  if (publishAt === null) return { ok: false, problem: 'missing' }
  if (publishAt <= now) return { ok: false, problem: 'past' }
  return { ok: true, publishAt, iso: new Date(publishAt).toISOString() }
}

// ----- The address tree -----------------------------------------------------------

export interface TreeNode<T> {
  page: T
  depth: number
}

interface Hangs {
  id: string
  parentId: string | null
  path: string
}

/**
 * The pages in tree order: a parent, then what hangs under it, each level by
 * address. A page whose parent is not in the set (filtered out, in the trash,
 * on a site's older shape) is shown as a root rather than lost; a cycle,
 * which the gateway refuses to create, cannot loop because each page is
 * placed once.
 */
export function buildPageTree<T extends Hangs>(pages: readonly T[]): TreeNode<T>[] {
  const ids = new Set(pages.map((page) => page.id))
  const byParent = new Map<string | null, T[]>()
  for (const page of pages) {
    const key = page.parentId !== null && ids.has(page.parentId) && page.parentId !== page.id ? page.parentId : null
    byParent.set(key, [...(byParent.get(key) ?? []), page])
  }
  const compare = (left: T, right: T) => left.path.localeCompare(right.path) || left.id.localeCompare(right.id)
  const placed = new Set<string>()
  const out: TreeNode<T>[] = []
  const place = (parent: string | null, depth: number) => {
    for (const page of [...(byParent.get(parent) ?? [])].sort(compare)) {
      if (placed.has(page.id)) continue
      placed.add(page.id)
      out.push({ page, depth })
      place(page.id, depth + 1)
    }
  }
  place(null, 0)
  // Only a cycle leaves pages unplaced; show them flat instead of hiding them.
  for (const page of [...pages].sort(compare)) {
    if (!placed.has(page.id)) { placed.add(page.id); out.push({ page, depth: 0 }) }
  }
  return out
}

/** Every page below `rootId`, at any depth. */
export function descendantIds(pages: readonly Hangs[], rootId: string): Set<string> {
  const found = new Set<string>()
  let frontier = [rootId]
  while (frontier.length > 0) {
    const next: string[] = []
    for (const page of pages) {
      if (page.parentId !== null && frontier.includes(page.parentId) && !found.has(page.id) && page.id !== rootId) {
        found.add(page.id)
        next.push(page.id)
      }
    }
    frontier = next
  }
  return found
}

/** The pages a page can hang under: not itself, not its own children, not the trash. */
export function parentCandidates<T extends Hangs & { status: PageStatus }>(pages: readonly T[], movingId: string | null): T[] {
  const blocked = movingId ? descendantIds(pages, movingId) : new Set<string>()
  return pages.filter((page) => page.id !== movingId && !blocked.has(page.id) && page.status !== 'trashed')
}

/** `/azienda` + `team` → `/azienda/team`; the home as parent contributes nothing. */
export function addressUnder(parentPath: string | null, slug: string): string {
  const base = parentPath === null || parentPath === '/' ? '' : parentPath.replace(/\/$/, '')
  return `${base}/${slug}`
}

// ----- Slug -----------------------------------------------------------------------

const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** i18n key suffix under `pages.problems`; null when the slug can be sent. */
export function slugProblem(slug: string): 'slugMissing' | 'slugInvalid' | null {
  const value = slug.trim()
  if (!value) return 'slugMissing'
  return SLUG.test(value) ? null : 'slugInvalid'
}

// ----- SEO ------------------------------------------------------------------------

export const SEO_TITLE_LIMIT = 70
export const SEO_DESCRIPTION_LIMIT = 200

export interface SeoDraft {
  metaTitle: string
  metaDescription: string
  ogImage: string
  canonical: string
  noindex: boolean
}

export function seoDraftFrom(seo: PageSeoDto): SeoDraft {
  return {
    metaTitle: seo.metaTitle ?? '',
    metaDescription: seo.metaDescription ?? '',
    ogImage: seo.ogImage ?? '',
    canonical: seo.canonical ?? '',
    noindex: seo.noindex === true,
  }
}

const TEXT_KEYS = ['metaTitle', 'metaDescription', 'ogImage', 'canonical'] as const

/**
 * What to send to `PATCH /seo`: only the fields that changed, and `null` for
 * one the person emptied (the gateway merges, so leaving a field out keeps
 * it). Unchecking «noindex» clears the field instead of storing `false`.
 */
export function seoPatch(original: PageSeoDto, draft: SeoDraft): Record<string, string | boolean | null> {
  const patch: Record<string, string | boolean | null> = {}
  for (const key of TEXT_KEYS) {
    const next = draft[key].trim()
    if (next === (original[key] ?? '')) continue
    patch[key] = next === '' ? null : next
  }
  const wasNoindex = original.noindex === true
  if (draft.noindex !== wasNoindex) patch.noindex = draft.noindex ? true : null
  return patch
}

export type CounterState = 'ok' | 'near' | 'over'

/** Past 90% of the limit is «near» (a search engine may cut it); past the limit the gateway refuses it. */
export function counterState(length: number, limit: number): CounterState {
  if (length > limit) return 'over'
  return length >= limit * 0.9 ? 'near' : 'ok'
}

/** i18n key suffixes under `pages.problems` for a draft the gateway would refuse. */
export function seoProblems(draft: SeoDraft): ('metaTitleTooLong' | 'metaDescriptionTooLong')[] {
  const problems: ('metaTitleTooLong' | 'metaDescriptionTooLong')[] = []
  if (draft.metaTitle.trim().length > SEO_TITLE_LIMIT) problems.push('metaTitleTooLong')
  if (draft.metaDescription.trim().length > SEO_DESCRIPTION_LIMIT) problems.push('metaDescriptionTooLong')
  return problems
}

// ----- History --------------------------------------------------------------------

/** `page.title.changed` → `titleChanged`, the key under `pages.event`. */
export function pageEventKey(type: string): string {
  const [, ...rest] = type.split('.')
  return rest.map((part, index) => index === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1)).join('')
}

// ----- Publish only this page -------------------------------------------------------

/**
 * A page that was live and is not any more (unpublished, or trashed after a
 * revision went public): the state says it, and its file may still be online.
 * Never published, or only scheduled, is not retired: there is nothing to remove.
 */
export function isRetiredPage(page: Pick<PageFacts, 'status' | 'publishedAt'>): boolean {
  return page.status !== 'published' && page.publishedAt !== null && page.publishedAt !== undefined
}

/**
 * «Pubblica solo questa pagina»: not for the trash, nor for a page waiting for
 * a future date. A retired page can use the same gesture to leave the site,
 * even from the trash: the button then removes instead of publishing.
 */
export function canDeployPage(page: Pick<PageFacts, 'status' | 'publishAt'> & { publishedAt?: number | null }, now: number): boolean {
  if (isRetiredPage({ status: page.status, publishedAt: page.publishedAt ?? null })) return true
  return page.status !== 'trashed' && scheduledFor({ status: 'published', publishAt: page.publishAt }, now) === null
}

export type DeployProblem = 'deliveryDisabled' | 'baselineMissing' | 'publisherDown'

/** The three refusals of the single-page deploy that deserve their own explanation; anything else shows the gateway's sentence. */
export function deployProblem(error: { status: number; code: string }): DeployProblem | null {
  if (error.status === 409 && error.code === 'site_delivery_disabled') return 'deliveryDisabled'
  if (error.status === 409 && error.code === 'incremental_baseline_unavailable') return 'baselineMissing'
  if (error.status === 503) return 'publisherDown'
  return null
}
